import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * Provider registry — the engine/region/capability/routing source of truth for
 * the telephony control plane. Backend: teeoff-cpass-backend /telephony/providers.
 */
export type EngineType = 'ASTERISK' | 'FREESWITCH';
export type RoutingMode = 'DIRECT' | 'KAMAILIO';
export type CapacityMode = 'SHARED_POOL' | 'PER_ORG';
export type NumberStatus = 'AVAILABLE' | 'ALLOCATED' | 'SUSPENDED' | 'RETIRED';

export interface ProviderCapabilities {
  supportsRefer?: boolean;
  supportsDial?: boolean;
  supportsConference?: boolean;
}

export interface Provider {
  id?: number;
  name: string;
  engine_type: EngineType;
  routing_mode?: RoutingMode;
  region?: string;
  control_url?: string;
  admin_token?: string;
  throttle_url?: string;
  sip_endpoint?: string;
  // Upstream carrier SIP trunk
  trunk_host?: string;
  trunk_port?: number | null;
  trunk_transport?: string;
  trunk_auth_mode?: string; // IP_WHITELIST | REGISTER
  trunk_username?: string;
  trunk_password?: string;       // write-only (sent on save, never returned)
  trunk_password_set?: boolean;  // read-only: whether a password is stored
  trunk_realm?: string;
  capabilities?: ProviderCapabilities;
  cps_limit?: number | null;
  max_channels?: number | null;
  capacity_mode?: CapacityMode;
  healthy?: boolean;
  last_checked_at?: string;
  last_healthy_at?: string;
  latency_ms?: number | null;
  health_detail?: string;
  enabled?: boolean;
  notes?: string;
  /** INTERNAL = runs on the platform's switches (master-managed); EXTERNAL = a carrier the org brought. */
  trunk_type?: 'INTERNAL' | 'EXTERNAL';
  /** Owning organization; null on Green Giraffe's own trunks (never sent to non-master callers). */
  organization_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ProviderOverview {
  id: number;
  name: string;
  engineType: EngineType;
  region?: string;
  routingMode?: RoutingMode;
  enabled: boolean;
  healthy: boolean;
  lastCheckedAt?: string;
  latencyMs?: number | null;
  healthDetail?: string;
  controlUrl?: string;
  sipEndpoint?: string;
  cpsLimit?: number | null;
  maxChannels?: number | null;
  capacityMode?: CapacityMode;
  liveChannels?: number;
  liveCps?: number;
  numbersTotal: number;
  numbersAvailable: number;
  numbersAllocated: number;
}

export interface ProviderNumber {
  id?: number;
  provider_id: number;
  e164: string;
  /**
   * The SWITCH's routing label, not the owner. Set by
   * /telephony/providers/numbers/{id}/allocate; `acquire` never touches it, so
   * after ownership moves this still names the previous tenant. See the
   * ownership fields below for who actually holds the number.
   */
  business_id?: string;
  status?: NumberStatus;
  /** Epoch millis on the wire, despite the sibling `*_at` strings elsewhere. */
  allocated_at?: number | string;
  label?: string;
  enabled?: boolean;

  // ── Ownership and commitment ───────────────────────────────────────────────
  // Sent by GET /telephony/providers/numbers on every row and previously not
  // declared here, so the fields that decide outbound caller ID were invisible
  // to every screen reading this type.
  /** cpass organization holding the number; absent while it sits in the pool. */
  purchased_by_organization_id?: string | null;
  purchased_at?: number | string | null;
  lock_in_months?: number | null;
  lock_in_starts_at?: number | string | null;
  lock_in_ends_at?: number | string | null;
  auto_renew?: boolean;
  renewal_count?: number;
  /** Server-computed: true while the holder is inside the committed term. */
  in_lock_in?: boolean;
  /** A number ANY organisation may present as outbound caller ID. */
  shared_demo?: boolean;
  created_at?: number | string;
  updated_at?: number | string;
}

export interface CapacitySummary {
  provider: string;
  capacityMode: CapacityMode;
  cpsLimit: number | null;
  maxChannels: number | null;
  cpsAllocatedToOrgs: number;
  channelsAllocatedToOrgs: number;
  cpsRemaining: number | null;
  channelsRemaining: number | null;
  orgSlices: number;
  numbersTotal: number;
  numbersAvailable: number;
  numbersAllocated: number;
}

export interface BusinessProviderConfig {
  id?: number;
  business_id: string;
  /** null for a non-master when the row rides a platform trunk (see platform_managed). */
  provider_id: number | null;
  /** Read-only: the trunk's name as the caller may see it — "Platform" when masked. */
  provider_name?: string | null;
  /** Read-only: true when the row rides a platform-managed trunk the caller cannot see. */
  platform_managed?: boolean;
  priority?: number;
  routing_mode_override?: RoutingMode | null;
  cps_limit?: number | null;
  max_channels?: number | null;
  config?: Record<string, any>;
  enabled?: boolean;
}

export interface ResolveResult {
  provider: string;
  engine: EngineType;
  routingMode: RoutingMode;
  capabilities: ProviderCapabilities;
}

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const providersService = {
  // providers
  list: async (): Promise<ApiResponse<Provider[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.PROVIDERS)).data; }
    catch (e: any) { return err(e, 'Failed to fetch providers'); }
  },
  /** `reason`: required by the server whenever cps_limit / max_channels are set (capacity history). */
  create: async (data: Provider, reason?: string): Promise<ApiResponse<Provider>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.PROVIDERS, data, { params: reason ? { reason } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to create provider'); }
  },
  /** `reason`: required by the server when cps_limit / max_channels change (capacity history). */
  update: async (id: number, data: Partial<Provider>, reason?: string): Promise<ApiResponse<Provider>> => {
    try { return (await axiosInstance.put(API_ENDPOINTS.PROVIDER_DETAIL(id), data, { params: reason ? { reason } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to update provider'); }
  },
  remove: async (id: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.PROVIDER_DETAIL(id))).data; }
    catch (e: any) { return err(e, 'Failed to delete provider'); }
  },

  // provider numbers (inventory)
  listNumbers: async (params?: { providerId?: number; businessId?: string; status?: NumberStatus }): Promise<ApiResponse<ProviderNumber[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.PROVIDER_NUMBERS, { params })).data; }
    catch (e: any) { return err(e, 'Failed to fetch numbers'); }
  },
  createNumber: async (data: ProviderNumber): Promise<ApiResponse<ProviderNumber>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.PROVIDER_NUMBERS, data)).data; }
    catch (e: any) { return err(e, 'Failed to add number'); }
  },
  bulkCreateNumbers: async (provider_id: number, numbers: string[]): Promise<ApiResponse<{ requested: number; created: number; skipped: string[] }>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDER_NUMBERS}/bulk`, { provider_id, numbers })).data; }
    catch (e: any) { return err(e, 'Failed to import numbers'); }
  },
  allocateNumber: async (id: number, business_id: string): Promise<ApiResponse<ProviderNumber>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDER_NUMBER_DETAIL(id)}/allocate`, { business_id })).data; }
    catch (e: any) { return err(e, 'Failed to allocate number'); }
  },
  releaseNumber: async (id: number): Promise<ApiResponse<ProviderNumber>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDER_NUMBER_DETAIL(id)}/release`, {})).data; }
    catch (e: any) { return err(e, 'Failed to release number'); }
  },
  removeNumber: async (id: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.PROVIDER_NUMBER_DETAIL(id))).data; }
    catch (e: any) { return err(e, 'Failed to delete number'); }
  },
  bulkAllocate: async (ids: number[], business_id: string): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDER_NUMBERS}/bulk-allocate`, { ids, business_id })).data; }
    catch (e: any) { return err(e, 'Bulk allocate failed'); }
  },
  bulkRelease: async (ids: number[]): Promise<ApiResponse<any>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDER_NUMBERS}/bulk-release`, { ids })).data; }
    catch (e: any) { return err(e, 'Bulk release failed'); }
  },
  capacity: async (providerId: number): Promise<ApiResponse<CapacitySummary>> => {
    try { return (await axiosInstance.get(`${API_ENDPOINTS.PROVIDERS}/${providerId}/capacity`)).data; }
    catch (e: any) { return err(e, 'Failed to fetch capacity'); }
  },

  // business routing
  listBusinessConfigs: async (businessId?: string): Promise<ApiResponse<BusinessProviderConfig[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.PROVIDER_BUSINESS_CONFIGS, { params: businessId ? { businessId } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to fetch business routing'); }
  },
  upsertBusinessConfig: async (data: BusinessProviderConfig, reason?: string): Promise<ApiResponse<BusinessProviderConfig>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.PROVIDER_BUSINESS_CONFIGS, data, { params: reason ? { reason } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to save routing'); }
  },
  removeBusinessConfig: async (id: number, reason?: string): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.PROVIDER_BUSINESS_CONFIG_DETAIL(id), { params: reason ? { reason } : {} })).data; }
    catch (e: any) { return err(e, 'Failed to delete routing'); }
  },

  resolve: async (businessId: string): Promise<ApiResponse<ResolveResult>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.PROVIDER_RESOLVE, { params: { businessId } })).data; }
    catch (e: any) { return err(e, 'Failed to resolve'); }
  },

  // health + overview
  overview: async (): Promise<ApiResponse<ProviderOverview[]>> => {
    try { return (await axiosInstance.get(`${API_ENDPOINTS.PROVIDERS}/overview`)).data; }
    catch (e: any) { return err(e, 'Failed to load overview'); }
  },
  healthCheck: async (id: number): Promise<ApiResponse<Provider>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDER_DETAIL(id)}/health-check`, {})).data; }
    catch (e: any) { return err(e, 'Health check failed'); }
  },
  healthCheckAll: async (): Promise<ApiResponse<Provider[]>> => {
    try { return (await axiosInstance.post(`${API_ENDPOINTS.PROVIDERS}/health-check-all`, {})).data; }
    catch (e: any) { return err(e, 'Health check failed'); }
  },
  usage: async (): Promise<ApiResponse<UsageRollup>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.TELEPHONY_USAGE)).data; }
    catch (e: any) { return err(e, 'Failed to load usage'); }
  }
};

export interface UsageRow { providerId: number | null; provider: string; calls: number; inbound: number; outbound: number; minutes: number; }
export interface UsageRollup { from: string; to: string; providers: UsageRow[]; totals: { calls: number; inbound: number; outbound: number; minutes: number }; }
